import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PermissionService } from '@zag/auth/ui';
import { ControlEnvironmentService } from '../services/control-environment.service';

/**
 * Guard that redirects to `/auth/no-env-access` when the authenticated user
 * has valid control-plane roles but has not been granted access to any
 * environment.
 *
 * Applied to the `select-environment` landing page and the `environments/:slug`
 * subtree so that users with no environment grants always land on the informational
 * page instead of an empty list or a confusing 404.
 *
 * Admin users always bypass this check — the API returns all environments for
 * them so their list is never empty.
 *
 * Run order: must be placed AFTER `controlAuthGuard` in the `canActivate` chain
 * so that authentication and permission state are already confirmed before we
 * check environment access.
 */
export const noEnvAccessGuard: CanActivateFn = async () => {
  const permissionService = inject(PermissionService);
  const envService = inject(ControlEnvironmentService);
  const router = inject(Router);

  // Wait for the permission state to be fully loaded (controlAuthGuard may have
  // already done this, but guard execution order is not guaranteed).
  if (!permissionService.isLoaded()) {
    await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
  }

  // If permissions have an error or the user has no permissions at all, let the
  // upstream guards handle it — don't double-redirect.
  if (permissionService.hasError() || permissionService.hasNoPermissions()) {
    return true;
  }

  // Wait for the environment list resource to finish its initial load.
  const resStatus = envService.environmentsResource.status;
  const isLoadingStatus = (s: string) => s === 'loading' || s === 'reloading';

  if (isLoadingStatus(resStatus())) {
    await firstValueFrom(toObservable(resStatus).pipe(filter(s => !isLoadingStatus(s))));
  }

  // If the resource errored we leave the user on the current route — error handling
  // is responsibility of the individual page components.
  if (resStatus() === 'error') {
    return true;
  }

  const environments = envService.environments();

  if (environments.length === 0) {
    return router.createUrlTree(['/auth/no-env-access']);
  }

  return true;
};
